/* ============================================================================
   AKINOD — Fiche produit classique
   ----------------------------------------------------------------------------
   Galerie, quantité, changement de variante, ajout au panier et barre collante.

   Principes tenus ici, par opposition à l'ancienne fiche configurateur :
   · le prix et la disponibilité existent déjà dans le HTML rendu par Liquid —
     ce script les met à jour, il ne les crée pas. Sans JS, la page reste juste ;
   · les erreurs s'affichent en ligne, jamais dans un `alert()` bloquant ;
   · le formulaire natif reste le chemin de repli si `fetch` échoue.
   ========================================================================== */

(() => {
  'use strict';

  const root = document.querySelector('[data-ak-pdp]');
  if (!root || root.dataset.akPdpBound) return;
  root.dataset.akPdpBound = '1';

  const $ = (sel, scope = root) => scope.querySelector(sel);

  /* --- Libellés ------------------------------------------------------------
     Rendus par la section dans la langue de la page : ce fichier ne passe pas
     par Liquid et ne peut donc pas appeler `t` lui-même. Les valeurs de repli
     ne servent que si la balise manque — un thème mal déployé, jamais un
     visiteur allemand. */
  const T = (() => {
    const repli = {
      ajouterAuPanier: 'Ajouter au panier',
      indisponible: 'Indisponible',
      enStock: 'En stock',
      ajoutEnCours: '…',
      ajoute: '✓',
      ajoutImpossible: 'Erreur',
    };
    try {
      return { ...repli, ...JSON.parse(document.getElementById('ak-pdp-textes').textContent) };
    } catch {
      return repli;
    }
  })();
  const $$ = (sel, scope = root) => Array.from(scope.querySelectorAll(sel));

  /* --- Galerie ------------------------------------------------------------ */

  const photos = $$('[data-ak-photo]');
  const thumbs = $$('[data-ak-thumb]');

  function showPhoto(index) {
    photos.forEach((p, i) => p.classList.toggle('is-active', i === index));
    thumbs.forEach((t, i) => {
      const on = i === index;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });
  }

  thumbs.forEach((t, i) => t.addEventListener('click', () => showPhoto(i)));

  // Flèches gauche/droite quand le focus est dans la bande de vignettes.
  $('.ak-pdp__thumbs')?.addEventListener('keydown', (e) => {
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const next = (current + dir + thumbs.length) % thumbs.length;
    showPhoto(next);
    thumbs[next].focus();
  });

  /* --- Quantité ------------------------------------------------------------ */

  const qtyInput = $('#ak-qty');
  // Le plafond vient du champ (réglage du thème, A-05) : `max` seul n'arrête ni
  // les boutons ni une valeur tapée au clavier.
  const qtyMax = Number(qtyInput?.max) || Infinity;
  const borner = (n) => Math.min(qtyMax, Math.max(1, n));

  $$('[data-ak-qty]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const delta = Number(btn.dataset.akQty);
      qtyInput.value = String(borner((Number(qtyInput.value) || 1) + delta));
    });
  });

  qtyInput?.addEventListener('change', () => {
    qtyInput.value = String(borner(Math.floor(Number(qtyInput.value) || 1)));
  });

  /* --- Variantes ----------------------------------------------------------- */

  const form = $('.ak-pdp__form');
  const variantField = $('[data-ak-variant-id]');
  const priceEl = $('[data-ak-price]');
  const priceCta = $('[data-ak-price-cta]');
  const priceSticky = $('[data-ak-price-sticky]');
  const stockEl = $('[data-ak-stock]');
  const addBtn = $('[data-ak-add]');
  const stickyBtn = $('[data-ak-sticky-add]');
  const victime = !!addBtn?.hasAttribute('data-ak-victime');

  // Le catalogue de variantes est sérialisé par la section elle-même, dans le
  // script JSON `[data-ak-variants]` : prix déjà formatés dans la devise et la
  // langue de la page, ce que le JSON de Shopify ne donne pas.
  let variants = [];
  try {
    const raw = document.querySelector('[data-ak-variants]')?.textContent;
    if (raw) variants = JSON.parse(raw);
  } catch { /* pas de catalogue : le formulaire natif reste fonctionnel */ }

  function currentOptions() {
    return $$('[data-ak-option]:checked').map((i) => i.value);
  }

  function matchVariant(opts) {
    return variants.find((v) => v.options.every((o, i) => o === opts[i]));
  }

  function applyVariant(v) {
    if (!v) return;
    variantField.value = v.id;

    const money = v.price_formatted ?? '';
    if (priceEl && money) priceEl.firstChild.nodeValue = money + ' ';
    if (priceCta && money) priceCta.textContent = money;
    if (priceSticky && money) priceSticky.textContent = money;

    // Une fiche « victime de son succès » garde son bouton fermé et sa mention,
    // quelle que soit la variante choisie.
    if (addBtn && !victime) {
      addBtn.disabled = !v.available;
      // Le même séparateur que le Liquid : le tiret long est banni depuis le 20/09.
      addBtn.innerHTML = v.available
        ? `${T.ajouterAuPanier} · <span data-ak-price-cta>${money}</span>`
        : T.indisponible;
    }
    // La barre collante suit : elle proposait « Ajouter » sur une variante épuisée.
    if (stickyBtn && !victime) stickyBtn.disabled = !v.available;

    /* La pastille et sa mention sont rendues par Liquid pour la variante
       d'arrivée ; sur une fiche à plusieurs variantes elles doivent suivre le
       choix, sans quoi le bouton annonce « Indisponible » sous une pastille
       verte. On réécrit donc les deux, avec les libellés que la section livre.
       La classe `is-out` posée jusqu'ici n'existait dans aucune règle. */
    if (stockEl) {
      stockEl.querySelector('.ak-dot')?.classList.toggle('ak-dot--out', !v.available);
      const mention = stockEl.querySelector('span:not(.ak-dot)');
      if (mention) mention.textContent = v.available ? T.enStock : T.indisponible;
    }

    // L'URL suit la variante : la page devient partageable et le retour arrière cohérent.
    const url = new URL(window.location);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({}, '', url);
  }

  $$('[data-ak-option]').forEach((input) => {
    input.addEventListener('change', () => applyVariant(matchVariant(currentOptions())));
  });

  /* --- Ajout au panier ------------------------------------------------------ */

  const errorEl = $('[data-ak-error]');

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (errorEl) errorEl.hidden = true;
  }

  /* Un ajout à la fois, et jamais sur une variante indisponible. Un double appui
     sur la barre collante envoyait l'article deux fois, et le second passage
     mémorisait « … » comme libellé du bouton principal, qui y restait jusqu'au
     rechargement. La même garde couvre Entrée dans le champ de quantité. */
  let ajoutEnCours = false;
  const libelleBarre = stickyBtn ? stickyBtn.innerHTML : '';

  /* Le bouton de la barre, seul à l'écran quand elle est visible, dit où en est
     l'ajout. Par un signe, « … » puis « ✓ » : un libellé traduit la débordait
     (« Zum Warenkorb hinzugefügt ✓ »). Le libellé entier reste son nom accessible. */
  function etatBarre(signe, nom) {
    if (!stickyBtn) return;
    if (signe === null) {
      stickyBtn.innerHTML = libelleBarre;
      stickyBtn.removeAttribute('aria-label');
      stickyBtn.disabled = false;
      return;
    }
    stickyBtn.textContent = signe;
    stickyBtn.setAttribute('aria-label', nom);
    stickyBtn.disabled = true;
  }

  async function addToCart(e) {
    e.preventDefault();
    // « Victime de son succès ! » (A-04) : un bouton désactivé n'empêche pas
    // Entrée dans le champ de quantité de soumettre le formulaire.
    if (victime) return;
    if (ajoutEnCours || addBtn.disabled) return;
    ajoutEnCours = true;
    clearError();

    const label = addBtn.innerHTML;
    addBtn.disabled = true;
    addBtn.textContent = T.ajoutEnCours;
    etatBarre('…', T.ajoutEnCours);

    const rendre = () => {
      addBtn.innerHTML = label;
      addBtn.disabled = false;
      etatBarre(null);
      ajoutEnCours = false;
    };

    try {
      const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${racine}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantField.value), quantity: Number(qtyInput?.value || 1) }],
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || T.ajoutImpossible);
      }

      addBtn.textContent = T.ajoute;
      etatBarre('✓', T.ajoute);

      // Rafraîchit le compteur du panier sans recharger la page, par l'en-tête : il
      // compte les étuis offerts et pas la pochette, comme la page du panier.
      if (window.AkinodPanier?.recompter) {
        window.AkinodPanier.recompter();
      } else {
        const cart = await fetch(`${racine}cart.js`).then((r) => r.json()).catch(() => null);
        const count = document.querySelector('[data-ak-cart-count]');
        if (cart && count) {
          count.textContent = cart.item_count;
          count.classList.toggle('is-empty', cart.item_count === 0);
        }
      }

      setTimeout(rendre, 1800);
    } catch (err) {
      showError(err.message);
      rendre();
      // L'erreur s'écrit sous le bouton principal, hors de l'écran quand la barre
      // est visible : on la ramène sous les yeux.
      const barre = stickyBtn?.closest('[data-ak-sticky]');
      if (barre && !barre.hidden) errorEl?.scrollIntoView({ block: 'center' });
    }
  }

  form?.addEventListener('submit', addToCart);
  stickyBtn?.addEventListener('click', (e) => {
    // La barre collante rejoue simplement la soumission du formulaire principal.
    form?.requestSubmit ? form.requestSubmit() : addToCart(e);
  });

  /* --- Barre collante -------------------------------------------------------- */

  const sticky = $('[data-ak-sticky]');

  if (sticky && addBtn && 'IntersectionObserver' in window) {
    // Elle n'apparaît que lorsque le vrai bouton d'achat a quitté l'écran.
    new IntersectionObserver(
      ([entry]) => { sticky.hidden = entry.isIntersecting; },
      { threshold: 0 },
    ).observe(addBtn);
  }
})();
